import { useCallback, useState } from "react";

export type CursorPaginationState = {
  /** Cursor for the current page; null for the first page. */
  after: string | null;
  /** True when there is a previous page to return to. */
  canGoPrevious: boolean;
  /** Advance to the next page using the current page's `endCursor`. No-op if null. */
  goNext: (endCursor: string | null) => void;
  /** Return to the previous page. */
  goPrevious: () => void;
  /** Reset back to the first page (e.g. when filters change). */
  reset: () => void;
};

/**
 * Client-side cursor stack for forward-only providers (Cognism only hands back a
 * cursor for the *next* page). Each stack entry is the `after` cursor used to
 * fetch that page; "Previous" pops the stack so React Query can serve the prior
 * cursor's page from cache.
 */
export function useCursorPagination(): CursorPaginationState {
  const [stack, setStack] = useState<(string | null)[]>([null]);

  const goNext = useCallback((endCursor: string | null) => {
    if (!endCursor) return;
    setStack((prev) => [...prev, endCursor]);
  }, []);

  const goPrevious = useCallback(() => {
    setStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
  }, []);

  const reset = useCallback(() => setStack([null]), []);

  return {
    after: stack[stack.length - 1],
    canGoPrevious: stack.length > 1,
    goNext,
    goPrevious,
    reset,
  };
}
